/* eslint-disable no-unused-vars */
import { forwardRef, useImperativeHandle, useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FormControl, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material';

import AgTextField from '../../stories/Components/AgTextField';
import AgTypography from '../../stories/Styles/AgTypography';

export enum FILTER_TYPE {  All,  Selected,  Unselected };
export type FilterSearchRef = { onClear: () => void };
export type FilterSearchProps = {
  isTouched: boolean;
  search: string;
  type: FILTER_TYPE
}

const initialValues = { 
  isTouched: false, 
  search: '', 
  type: FILTER_TYPE.All
};

type FilterSearchComponentProps = { placeholder?: string; setData: (arg: FilterSearchProps) => void };

const AgFilterSearch = forwardRef<FilterSearchRef, FilterSearchComponentProps>(( { placeholder, setData }, ref) => {
  const { formatMessage } = useIntl();
  
  const [values, setValues] = useState<FilterSearchProps>(initialValues);

  const options = useMemo(() => [ 
    { label: formatMessage({ id: 'FILTER_SEARCH_ALL' }), value: FILTER_TYPE.All },
    { label: formatMessage({ id: 'FILTER_SEARCH_SELECTED' }), value: FILTER_TYPE.Selected },
    { label: formatMessage({ id: 'FILTER_SEARCH_UNSELECTED' }), value: FILTER_TYPE.Unselected }
  ], [formatMessage]);

  const handleChange = useCallback((key: string, value: string | FILTER_TYPE) => {
    setValues(prev => ({ ...prev, isTouched: true, [key]: value }));
  }, []);

  useEffect(() => {
    setData && setData(values);
  }, [setData, values]);

  useImperativeHandle(ref, () => ({
    onClear: () => setValues(initialValues)
  }), []);

  return (
    <Stack 
      direction='row' 
      alignItems='center' 
      gap={3}
      sx={{ '& input::placeholder': { fontSize: 14 } }}
    >
      <AgTextField
        value={values.search}
        onChange={(event) => handleChange('search', event.target.value || '')}
        placeholder={formatMessage({ id: placeholder || 'FILTER_SEARCH_PLACEHOLDER' })} 
        style={{ minWidth: 200 }}
      />

      <FormControl>
        <RadioGroup 
          row
          value={values.type}
          onChange={(event) => handleChange('type', Number(event.target.value) ?? FILTER_TYPE.All)}
        >
          {options.map(({ label, value }) => (
            <FormControlLabel 
              key={label} 
              value={value} 
              control={<Radio size='small' />} 
              label={<AgTypography contentStyle='AP.Paragraph.Small.Regular' message={label} />}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Stack>
  );
});

export default AgFilterSearch;